import React, { useMemo, useState } from "react";
import { TextExtractor } from "../../internal/TextExtractor";
import { SiteState } from "../../../state/state-models";
import { useSiteContext } from "../../../state/SiteContext";

interface FormatDateProps {
    language?: string,
    children: React.ReactNode,
}

/***
 * A source component that formats a date value in a child element.
 * The date is expected to be a number representing a timestamp.
 * The date is formatted using the Intl.DateTimeFormat API.
 * 
 * Params:
 *      language: The language to format the date in
 */
export const FormatDate = React.memo(function FormatDate({ language, children }: FormatDateProps): React.ReactElement {
    const { siteState } = useSiteContext();

    // Get the number value as a string from the children
    const [dateValue, setDateValue] = useState("");

    const formattedDate = useMemo(() => {
        if (dateValue) {
            const lang = language || ((siteState as SiteState).site?.defaults?.language || "en");
            // Ensure lang is a valid locale by checking if it exists in the available locales
            const validLang = Intl.DateTimeFormat.supportedLocalesOf([lang])[0] || "en";
            const numericalValue = parseInt(dateValue);
            if (!isNaN(numericalValue)) {
                return new Date(numericalValue).toLocaleDateString(validLang);
            }
        }
        return "";
    }, [dateValue, language, siteState]);


    return <>
        {formattedDate}
        <TextExtractor onExtractedText={setDateValue}>
            {children}
        </TextExtractor>
    </>;
});